import { ShieldCheck, Target, HeartHandshake, Award, CheckCircle2 } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";
import { PageHero, CTABand } from "@/components/Layout";
import { TrustBar } from "@/components/Sections";
import { OutlineButton } from "@/components/CTAButtons";
import { business, IMG } from "@/data/site";

const About = () => {
  useSeo({
    title: `About Us — Trusted New York Contractor | ${business.name}`,
    description: `Learn about ${business.name}, a licensed and insured New York plumbing, electrical & HVAC contractor with ${business.yearsInBusiness}+ years serving local homeowners.`,
  });

  const values = [
    { icon: Target, t: "Our Mission", d: "To be New York's most trusted home service contractor by treating every home like our own and every customer like a neighbor." },
    { icon: HeartHandshake, t: "Our Values", d: "Honesty, craftsmanship, and respect. We show up on time, tell you the truth, and stand behind our work." },
    { icon: ShieldCheck, t: "Our Promise", d: "Licensed, insured, and guaranteed. If something isn't right, we make it right — no arguments, no excuses." },
  ];

  return (
    <>
      <PageHero eyebrow="Our Story" title="A Local New York Contractor You Can Trust" subtitle={`For ${business.yearsInBusiness}+ years we've kept New York homes safe, comfortable, and running — one honest job at a time.`} />
      <TrustBar />

      <section className="max-w-7xl mx-auto px-4 py-16 sm:py-24 grid lg:grid-cols-2 gap-12 items-center" data-testid="about-story">
        <div>
          <p className="text-sm uppercase tracking-[0.2em] font-semibold text-[#059669] mb-3">Who We Are</p>
          <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight mb-5">Built on Reliability, Grown by Referrals</h2>
          <div className="space-y-4 text-[#475569] text-lg leading-relaxed">
            <p>{business.name} started with a simple frustration: too many homeowners couldn't find a contractor who showed up on time, quoted fairly, and did the job right. So we built the company we wished existed.</p>
            <p>Today, our licensed plumbers, electricians, and HVAC technicians serve homeowners and property owners across all five boroughs. We've earned hundreds of five-star reviews the hard way — by doing great work and treating people right.</p>
            <p>We're not the biggest company in New York, and we don't want to be. We'd rather be the one your family, neighbors, and building manager keep recommending.</p>
          </div>
          <div className="mt-8"><OutlineButton to="/contact" testid="about-contact-btn">Get in Touch</OutlineButton></div>
        </div>
        <div className="relative">
          <img src={IMG.hvac} alt="Empire Home Services technician at work in New York" loading="lazy" className="w-full aspect-[4/3] object-cover border-2 border-[#0F2E4C]" />
          <div className="absolute -bottom-6 -left-6 bg-[#059669] text-white p-6 hidden sm:block">
            <div className="font-display font-black text-4xl">{business.yearsInBusiness}+</div>
            <div className="text-sm uppercase tracking-wide">Years Serving NY</div>
          </div>
        </div>
      </section>

      <section className="bg-white border-y border-[#E2E8F0]" data-testid="about-values">
        <div className="max-w-7xl mx-auto px-4 py-16 sm:py-24">
          <div className="grid md:grid-cols-3 gap-6">
            {values.map((v) => (
              <div key={v.t} className="border border-[#E2E8F0] p-8 hover:shadow-[6px_6px_0px_#0F2E4C] transition-shadow">
                <div className="w-14 h-14 bg-[#0F2E4C] flex items-center justify-center mb-5"><v.icon className="w-7 h-7 text-white" /></div>
                <h3 className="font-display font-bold text-2xl mb-3">{v.t}</h3>
                <p className="text-[#475569] leading-relaxed">{v.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 py-16 sm:py-24" data-testid="about-credentials">
        <div className="text-center mb-10">
          <Award className="w-12 h-12 text-[#059669] mx-auto mb-4" />
          <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight mb-4">Licensed, Insured & Credentialed</h2>
          <p className="text-[#475569] text-lg">We hold ourselves to the highest professional standards in New York.</p>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {[
            "Fully licensed New York contractor",
            "Comprehensive liability & workers' comp insurance",
            "Background-checked, uniformed technicians",
            "Manufacturer-certified installation partners",
            "Workmanship guarantee on every job",
            "Transparent, upfront pricing policy",
          ].map((c) => (
            <div key={c} className="flex items-start gap-3 bg-[#FAFAFA] border border-[#E2E8F0] p-4"><CheckCircle2 className="w-5 h-5 text-[#16A34A] shrink-0 mt-0.5" /> <span className="text-[#0F2E4C] font-medium">{c}</span></div>
          ))}
        </div>
        <p className="text-xs text-[#475569] mt-6 text-center italic">Note: {business.license} — replace with the client's real, verified license numbers before launch.</p>
      </section>

      <CTABand />
    </>
  );
};

export default About;
